import appConfig from "@core/appconfig";
import { Facebook, Instagram, Mail, MapPin, Twitter } from "lucide-react";
import Link from "next/link";
import { FaWhatsappSquare } from "react-icons/fa";

const FOOTER_BG = '#0B3B2E';
const ACCENT = '#5FD1AE';

// Footer for the stays listing pages (city + all-stays). Same single-entity
// identity as HotelFooter's unbranded mode: Jhattse (brand) operated by
// Esangrah Technologies Pvt. Ltd. (legal entity).
export const StaysFooter = () => {
    const BRAND = appConfig.appName || 'Jhattse';
    const LEGAL = 'Esangrah Technologies Pvt. Ltd.';
    const CIN = appConfig.cin || 'U72900UR2023PTC015183';
    const GSTIN = appConfig.gstin || '05AAHCE4252A1Z7';
    const REG_ADDR = appConfig.registeredAddress || 'Natthanpur, Dehradun, Uttarakhand 248005';
    const email = appConfig.email || 'connect@jhattse.com';
    const hasSocial = appConfig?.twitter?.length > 0 || appConfig?.facebook?.length > 0 || appConfig?.instagram?.length > 0 || appConfig?.mapPin?.length > 0;

    const linkCls = "text-sm font-medium text-white/70 visited:text-white/70 hover:text-white visited:hover:text-white transition-colors";
    const headCls = "text-xs font-semibold text-white/50 uppercase tracking-wider select-none";
    const socialCls = "text-white/60 visited:text-white/60 hover:text-white visited:hover:text-white transition-colors";

    return (
        <footer className="flex flex-col justify-center w-full font-manrope" style={{ backgroundColor: FOOTER_BG }}>
            <div className="flex flex-col box-border md:flex-row md:flex-wrap md:justify-between gap-6 p-5 md:p-8 max-w-[1240px] w-full mx-auto">

                {/* Brand + legal entity */}
                <div className="flex flex-col justify-start gap-2 max-w-sm">
                    <div className="text-white font-bold text-xl">
                        <Link href="/" prefetch={false} className="text-white visited:text-white">{BRAND}</Link>
                    </div>
                    <div className="text-white/70 text-sm">
                        Operated by <span className="font-semibold text-white">{LEGAL}</span>
                    </div>
                    <div className="text-white/60 text-xs leading-relaxed">
                        <div>CIN: {CIN}</div>
                        <div>GSTIN: {GSTIN}</div>
                        <div className="flex items-start gap-1.5 mt-1">
                            <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: ACCENT }} />
                            <span>Registered office: {REG_ADDR}</span>
                        </div>
                    </div>
                    {hasSocial && (
                        <div className="flex flex-row items-center gap-3 mt-1">
                            {appConfig?.twitter?.length > 0 && <Link href={appConfig?.twitter} className={socialCls}><Twitter size={18} /></Link>}
                            {appConfig?.facebook?.length > 0 && <Link href={appConfig?.facebook} className={socialCls}><Facebook size={18} /></Link>}
                            {appConfig?.instagram?.length > 0 && <Link href={appConfig?.instagram} className={socialCls}><Instagram size={18} /></Link>}
                            {appConfig?.mapPin?.length > 0 && <Link href={appConfig?.mapPin} className={socialCls}><MapPin size={18} /></Link>}
                        </div>
                    )}
                </div>

                <div className="flex flex-col sm:flex-row md:justify-end gap-6 md:gap-12">
                    {/* Useful links */}
                    <div className="flex flex-col gap-1.5">
                        <div className={headCls}>Useful links</div>
                        <div className="flex flex-col gap-1.5 sm:w-44">
                            <Link href="/stays" className={linkCls}>All Hotels &amp; Stays</Link>
                            <Link href="/stays/about" prefetch={false} className={linkCls}>About {BRAND}</Link>
                            <Link href="/terms" prefetch={false} className={linkCls}>Terms of Use</Link>
                            <Link href="/privacy" prefetch={false} className={linkCls}>Privacy Policy</Link>
                            <Link href="/refund" prefetch={false} className={linkCls}>Refund Policy</Link>
                        </div>
                    </div>

                    {/* Contact */}
                    <div className="flex flex-col gap-2 min-w-0">
                        <div className={headCls}>Get in touch</div>
                        <div className="flex flex-col gap-2">
                            <Link href={`mailto:${email}`} className={`${linkCls} flex flex-row items-center gap-2 break-all`}>
                                <Mail size="1em" className="flex-shrink-0" style={{ color: ACCENT }} />{email}
                            </Link>
                            {appConfig.phone && (
                                <Link
                                    href={`https://wa.me/${appConfig.phone.replace(/\D/g, "")}?text=Hello%2C%20I%20have%20a%20query%20about%20booking%20a%20stay.`}
                                    className={`${linkCls} flex flex-row items-center gap-2`}
                                >
                                    <FaWhatsappSquare size="1.15em" className="flex-shrink-0" style={{ color: ACCENT }} />{appConfig.phone}
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className="text-white/40 text-center text-xs min-h-10 py-2 px-4 w-full box-border flex flex-col justify-center select-none border-t border-white/10">
                {`© ${new Date().getFullYear()} ${LEGAL} · ${BRAND} is a brand of ${LEGAL}`}
            </div>
        </footer>
    );
};
